import React from 'react';
import { Briefcase, User } from 'lucide-react';
import { useAudience } from '@/context/AudienceContext';
import { TID } from '@/testIds';

/**
 * Segmented audience toggle. Two sizes:
 *  - compact  → header docked (tighter padding, no eyebrow label)
 *  - default  → in-flow (with "VIEWING AS" eyebrow above)
 */
export default function AudienceToggle({ compact = false }) {
  const { audience, setAudience } = useAudience();

  const opts = [
    { key: 'b2b',    label: 'B2B Partner',     Icon: Briefcase },
    { key: 'retail', label: 'Retail Customer', Icon: User },
  ];

  const sizeCls = compact
    ? 'px-3 py-1.5 text-[11px] gap-1.5'
    : 'px-5 py-2.5 text-sm gap-2';

  const wrap = compact
    ? 'inline-flex'
    : 'inline-flex flex-col gap-1.5';

  return (
    <div className={wrap}>
      {!compact && (
        <span className="mi-eyebrow text-[10px] text-white/50" data-testid="audience-toggle-label">
          // VIEWING AS
        </span>
      )}
      <div
        role="tablist"
        aria-label="Choose audience"
        className="inline-flex items-stretch border-2 border-white/15 bg-black/70 font-mono-data uppercase tracking-widest shadow-[0_0_0_1px_rgba(0,0,0,0.6)_inset] hover:border-[#E63946]/50 transition-colors"
      >
        {opts.map(({ key, label, Icon }) => {
          const active = audience === key;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={active}
              onClick={() => setAudience(key)}
              data-testid={key === 'b2b' ? TID.audienceB2B : TID.audienceRetail}
              className={[
                'relative inline-flex items-center justify-center font-bold whitespace-nowrap',
                'transition-all duration-200',
                sizeCls,
                active
                  ? 'bg-[#E63946] text-white shadow-[0_0_18px_-4px_rgba(230,57,70,0.6)]'
                  : 'text-white/70 hover:text-white hover:bg-white/[0.04]',
              ].join(' ')}
            >
              <Icon className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
              {label}
              {active && (
                <span
                  aria-hidden="true"
                  className="absolute -bottom-[3px] left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-[#E63946] rotate-45 border border-black"
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
